<template>
  <!--
    Optional from-to range for an Adventure export and the date columns it applies to.
    A user is exported when any of the ticked dates falls into the range.
  -->
  <div class="adventureExportDateFilter pt-2">
    <label class="d-block mb-1">{{ $t('from') }} - {{ $t('until') }}</label>
    <date-picker
      :value="[value.from || null, value.to || null]"
      :value-type="dayValueType"
      :shortcuts="shortcuts"
      :first-day-of-week="1"
      range
      confirm
      range-separator="-"
      type="date"
      lang="en"
      format="DD.MM.YYYY"
      class="date-range"
      @input="updateRange"
    />
    <div class="date-fields pt-2">
      <div
        v-for="o in options"
        :key="o.key"
        class="form-check"
      >
        <input
          :id="idPrefix + '-date-field-' + o.key"
          :checked="value.dateFields.includes(o.key)"
          class="form-check-input"
          type="checkbox"
          @change="toggleDateField(o.key, $event.target)"
        >
        <label
          class="form-check-label"
          :for="idPrefix + '-date-field-' + o.key"
        >{{ o.label }}</label>
      </div>
    </div>
  </div>
</template>

<script>
import DatePicker from "vue2-datepicker";

function toDay(date) {
  const pad = (n) => String(n).padStart(2, "0");
  const month = pad(date.getMonth() + 1);
  return `${date.getFullYear()}-${month}-${pad(date.getDate())}`;
}

// Quick picks for periodic reports. Worked out on click, so a page left open over a month
// end still picks the right month.
function setRange(picker, start, end) {
  picker.currentValue = [start, end];
  picker.updateDate(true);
}

export default {
  name: "AdventureExportDateFilter",
  components: {
    DatePicker,
  },
  props: {
    // { from: "yyyy-MM-dd" | "", to: "yyyy-MM-dd" | "", dateFields: [field keys] }
    value: {
      type: Object,
      required: true,
    },
    // The date columns of the export, as { key, label }
    options: {
      type: Array,
      required: true,
    },
    idPrefix: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      // The API takes whole days as yyyy-MM-dd, in local time like the export shows them
      dayValueType: {
        value2date: (day) => {
          if (!day) return null;
          const [year, month, date] = day.split("-").map(Number);
          return new Date(year, month - 1, date);
        },
        date2value: (date) => (date ? toDay(date) : ""),
      },
      shortcuts: [
        {
          text: "This month",
          onClick: (picker) => {
            const now = new Date();
            setRange(
              picker,
              new Date(now.getFullYear(), now.getMonth(), 1),
              new Date(now.getFullYear(), now.getMonth() + 1, 0)
            );
          },
        },
        {
          text: "Last month",
          onClick: (picker) => {
            const now = new Date();
            setRange(
              picker,
              new Date(now.getFullYear(), now.getMonth() - 1, 1),
              new Date(now.getFullYear(), now.getMonth(), 0)
            );
          },
        },
        {
          text: "This year",
          onClick: (picker) => {
            const now = new Date();
            setRange(
              picker,
              new Date(now.getFullYear(), 0, 1),
              new Date(now.getFullYear(), 11, 31)
            );
          },
        },
      ],
    };
  },
  methods: {
    update(change) {
      this.$emit("input", { ...this.value, ...change });
    },
    // Clearing the picker gives [ "", "" ], which means no range
    updateRange([from, to]) {
      this.update({ from: from || "", to: to || "" });
    },
    // At least one date stays ticked, so a range always has something to apply to. Unticking
    // the last one is undone rather than prevented by disabling it, which would grey it out
    // as if it were not in use.
    toggleDateField(key, checkbox) {
      if (!checkbox.checked && this.value.dateFields.length === 1) {
        checkbox.checked = true;
        return;
      }
      const dateFields = checkbox.checked
        ? [...this.value.dateFields, key]
        : this.value.dateFields.filter((x) => x !== key);
      this.update({ dateFields });
    },
  },
};
</script>

<style scoped lang="scss">
.adventureExportDateFilter {
  .date-range {
    width: 100%;
    max-width: 360px;
  }
  .date-fields {
    column-width: 180px;
  }
}
</style>
